var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable no-redeclare */
import { ImageFormat, isRaster } from '@design-stack-ct/assets-sdk';
import { useCallback } from 'react';
import { useFileMethods, useUploadManager } from '../../UploadManagerProvider';
import { FileTypeNotSupportedError, MaxUploadsExceededError } from '../errors';
import { MaxFileSizeExceededError } from '../errors/MaxFileSizeExceededError';
import { useUploadConfiguration } from '../UploadConfigurationProvider';
import { UploadingAnnouncementKeys } from '../../UploadManagerProvider/UploadManagerProvider';
const extension = (fileName) => {
    const splitName = fileName.split('.');
    return splitName[splitName.length - 1].toLocaleLowerCase();
};
const BYTES_TO_KB = 1024;
/**
 * Hook returns a function uploadFiles for uploading multiple files
 */
export const useUploadFiles = () => {
    const { uploadFile } = useFileMethods();
    const { onUpload, onError, setUploadingAnnouncement } = useUploadManager();
    const { supportedFileTypes: { fileExtensions, fileExtensionsAsString }, } = useUploadConfiguration();
    const { maxFileSizeInKilobytes, maxUploads } = useUploadConfiguration();
    function uploadFiles(arg, onUploadStart) {
        return __awaiter(this, void 0, void 0, function* () {
            let files;
            let onBeforeUploadCallback;
            let onUploadStartCallback = onUploadStart;
            if (Array.isArray(arg) || arg instanceof FileList) {
                files = arg;
            }
            else {
                files = arg.files;
                onUploadStartCallback = arg.onUploadStart;
                onBeforeUploadCallback = arg.onBeforeUpload;
            }
            if (maxUploads > 0 && files.length > maxUploads) {
                const error = new MaxUploadsExceededError(`Only ${maxUploads} can be uploaded at a time.`, files);
                if (onError) {
                    onError(error);
                    return;
                }
                throw error;
            }
            if (files && files.length > 0) {
                const validFiles = [];
                const invalidFiles = [];
                const validationErrors = [];
                // Validate all files
                for (let i = 0; i < files.length; i += 1) {
                    const file = files[i];
                    try {
                        // Extenstion check
                        if (!fileExtensions.includes(extension(file === null || file === void 0 ? void 0 : file.name))) {
                            validationErrors.push(new FileTypeNotSupportedError(`"${file === null || file === void 0 ? void 0 : file.name}" is not using a supported extension (${fileExtensionsAsString}).`, file));
                            invalidFiles.push(file);
                            continue;
                        }
                        // Size check
                        if (maxFileSizeInKilobytes && file.size / BYTES_TO_KB > maxFileSizeInKilobytes) {
                            validationErrors.push(new MaxFileSizeExceededError(`"${file === null || file === void 0 ? void 0 : file.name}" exceeds the max file size ${maxFileSizeInKilobytes} File size ${file.size / BYTES_TO_KB}.`, file));
                            invalidFiles.push(file);
                            continue;
                        }
                        validFiles.push(file);
                    }
                    catch (error) {
                        validationErrors.push(error);
                        invalidFiles.push(file);
                    }
                }
                // Report validation errors
                validationErrors.forEach((error) => {
                    if (onError) {
                        onError(error);
                    }
                    else {
                        throw error;
                    }
                });
                onBeforeUploadCallback === null || onBeforeUploadCallback === void 0 ? void 0 : onBeforeUploadCallback(validFiles);
                // Proceed with uploading valid files
                if (validFiles.length > 0) {
                    try {
                        const results = [];
                        const uploads = [];
                        for (const file of validFiles) {
                            const abortController = new AbortController();
                            const isImageInfoSupported = (isRaster(file) && !file.type.includes(ImageFormat.Tiff)) ||
                                file.type.includes(ImageFormat.Pdf);
                            let imageInfoResolve;
                            let imageInfoReject;
                            const imageInfoPromise = new Promise((res, rej) => {
                                imageInfoResolve = res;
                                imageInfoReject = rej;
                                if (!isImageInfoSupported) {
                                    imageInfoResolve(undefined);
                                }
                            });
                            let tempPreviewUrlResolve;
                            let tempPreviewUrlReject;
                            const tempPreviewUrlPromise = new Promise((res, rej) => {
                                tempPreviewUrlResolve = res;
                                tempPreviewUrlReject = rej;
                            });
                            const promise = uploadFile(file, {
                                signal: abortController.signal,
                                callback: isImageInfoSupported
                                    ? (event) => {
                                        if (event.imageInfo) {
                                            imageInfoResolve(event.imageInfo);
                                        }
                                        if (event.tempPreviewUrl) {
                                            tempPreviewUrlResolve(event.tempPreviewUrl);
                                        }
                                        if (event.isError) {
                                            abortController.abort();
                                            const error = new FileTypeNotSupportedError(`"${file === null || file === void 0 ? void 0 : file.name}" is corrupted or not supported.`, file);
                                            onError === null || onError === void 0 ? void 0 : onError(error);
                                            setUploadingAnnouncement(UploadingAnnouncementKeys.UploadingError);
                                            if (onUploadStartCallback) {
                                                imageInfoReject(error);
                                                tempPreviewUrlReject(error);
                                                return;
                                            }
                                            imageInfoResolve(undefined);
                                            tempPreviewUrlResolve(undefined);
                                        }
                                    }
                                    : undefined,
                            });
                            uploads.push(promise);
                            results.push({
                                upload: promise,
                                file,
                                abort: () => abortController.abort(),
                                signal: abortController.signal,
                                imageInfo: imageInfoPromise,
                                tempPreviewUrl: tempPreviewUrlPromise,
                            });
                        }
                        onUpload === null || onUpload === void 0 ? void 0 : onUpload(uploads, validFiles, invalidFiles);
                        setUploadingAnnouncement(UploadingAnnouncementKeys.UploadingStart);
                        onUploadStartCallback === null || onUploadStartCallback === void 0 ? void 0 : onUploadStartCallback(results);
                        yield Promise.all(uploads);
                    }
                    catch (e) {
                        if (onError) {
                            onError(e);
                        }
                        else {
                            throw e;
                        }
                    }
                }
            }
        });
    }
    const uploadFilesCb = useCallback(uploadFiles, [
        uploadFiles,
        uploadFile,
        fileExtensions,
        fileExtensionsAsString,
        onError,
        onUpload,
        maxUploads,
        maxFileSizeInKilobytes,
    ]);
    return uploadFilesCb;
};
//# sourceMappingURL=useUploadFiles.js.map